/* SaveTemplateModal.tsx — "Save as Template" modal
   Lets the user name a template, choose how many signers it will have,
   assign fields to signers via drag-and-drop (or click), and rename
   signer roles inline. */

import React, { useState, useMemo, useEffect, useRef } from "react";
import "./SaveTemplateModal.css";

/* ----------------------------- types ----------------------------- */

export interface SaveTemplateModalProps {
  isOpen: boolean;
  doc: any[];
  defaultName: string;
  onClose: () => void;
  onSave: (data: {
    name: string;
    signerLabels: Record<string, string>;
    doc: any[];
    templateKind: "template" | "quicksend";
    roles: Array<{ key: string; label: string; fieldIds: string[] }>;
    senderFields: Array<{ fieldId: string; label: string; type: string }>;
  }) => void;
  saving?: boolean;
}

interface FieldInfo {
  id: string;
  kind: "field" | "signature";
  label: string;
  pageIndex: number;
}

/* ----------------------------- constants ----------------------------- */

const SIGNER_COLORS = [
  { dark: "#2563eb", soft: "#eff6ff" },
  { dark: "#7c3aed", soft: "#f5f3ff" },
  { dark: "#059669", soft: "#ecfdf5" },
  { dark: "#dc2626", soft: "#fef2f2" },
  { dark: "#d97706", soft: "#fffbeb" },
  { dark: "#0891b2", soft: "#ecfeff" },
  { dark: "#4f46e5", soft: "#eef2ff" },
  { dark: "#be185d", soft: "#fdf2f8" },
];

/* ----------------------------- helpers ----------------------------- */

function extractAllFields(doc: any[]): FieldInfo[] {
  const fields: FieldInfo[] = [];
  for (let pi = 0; pi < doc.length; pi++) {
    const page = doc[pi];
    const els: any[] = Array.isArray(page?.elements) ? page.elements : [];
    for (const el of els) {
      if (!el || !el.id) continue;
      if (el.kind !== "field" && el.kind !== "signature") continue;
      fields.push({
        id: el.id,
        kind: el.kind,
        label:
          el.label ||
          (el.kind === "signature" ? "Signature" : el.type || "Field"),
        pageIndex: pi,
      });
    }
  }
  return fields;
}

/* ----------------------------- component ----------------------------- */

const SaveTemplateModal: React.FC<SaveTemplateModalProps> = ({
  isOpen,
  doc,
  defaultName,
  onClose,
  onSave,
  saving = false,
}) => {
  const [templateName, setTemplateName] = useState("");
  const [signerCount, setSignerCount] = useState(1);
  const [signerLabels, setSignerLabels] = useState<Record<string, string>>({});
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const [dragOverTarget, setDragOverTarget] = useState<string | null>(null);
  const [mode, setMode] = useState<"template" | "quicksend">("template");
  const [senderFills, setSenderFills] = useState<Record<string, boolean>>({});
  const nameInputRef = useRef<HTMLInputElement | null>(null);

  const allFields = useMemo(
    () => (isOpen ? extractAllFields(doc) : []),
    [isOpen, doc]
  );

  // Initialize form on open
  useEffect(() => {
    if (!isOpen) return;
    setTemplateName(defaultName || "Untitled Template");
    setDragOverTarget(null);
    setMode("template");

    // Pre-populate senderFills from element data
    const initialSenderFills: Record<string, boolean> = {};
    for (const page of doc) {
      const els: any[] = Array.isArray(page?.elements) ? page.elements : [];
      for (const el of els) {
        if (el?.senderFill) initialSenderFills[el.id] = true;
      }
    }
    setSenderFills(initialSenderFills);

    // Detect any existing signer assignments in the doc
    const orderMap = new Map<string, number>();
    const labelMap = new Map<string, string>();
    const existingAssignments: Record<string, string> = {};
    let nextOrder = 1;

    for (const page of doc) {
      const els: any[] = Array.isArray(page?.elements) ? page.elements : [];
      for (const el of els) {
        if (!el || !el.id) continue;
        if (el.kind !== "field" && el.kind !== "signature") continue;
        const sid = el.signerId;
        if (sid && !orderMap.has(sid)) {
          orderMap.set(sid, nextOrder);
          labelMap.set(
            sid,
            el.assignee || el.assigneeEmail || `Signer ${nextOrder}`
          );
          nextOrder++;
        }
        if (sid && orderMap.has(sid)) {
          existingAssignments[el.id] = `signer-${orderMap.get(sid)}`;
        }
      }
    }

    const count = Math.max(1, orderMap.size || 1);
    setSignerCount(count);

    const labels: Record<string, string> = {};
    for (let i = 1; i <= count; i++) {
      labels[`signer-${i}`] = `Signer ${i}`;
    }
    for (const [origId, label] of labelMap) {
      const num = orderMap.get(origId);
      if (num) labels[`signer-${num}`] = label;
    }
    setSignerLabels(labels);
    setAssignments(existingAssignments);

    requestAnimationFrame(() => {
      nameInputRef.current?.focus();
      nameInputRef.current?.select();
    });
  }, [isOpen, doc, defaultName]);

  // Keep labels and assignments in sync when signer count changes
  useEffect(() => {
    setSignerLabels((prev) => {
      const next = { ...prev };
      for (let i = 1; i <= signerCount; i++) {
        if (!next[`signer-${i}`]) next[`signer-${i}`] = `Signer ${i}`;
      }
      for (const key of Object.keys(next)) {
        const num = parseInt(key.split("-")[1], 10);
        if (num > signerCount) delete next[key];
      }
      return next;
    });
    setAssignments((prev) => {
      const next = { ...prev };
      for (const [fieldId, signerId] of Object.entries(next)) {
        const num = parseInt(signerId.split("-")[1], 10);
        if (num > signerCount) delete next[fieldId];
      }
      return next;
    });
  }, [signerCount]);

  // Derived
  const unassignedFields = allFields.filter((f) => !assignments[f.id]);
  const signers = Array.from({ length: signerCount }, (_, i) => ({
    id: `signer-${i + 1}`,
    label: signerLabels[`signer-${i + 1}`] || `Signer ${i + 1}`,
    color: SIGNER_COLORS[i % SIGNER_COLORS.length],
    fields: allFields.filter((f) => assignments[f.id] === `signer-${i + 1}`),
  }));

  /* ---- Drag and drop ---- */

  const handleDragStart = (e: React.DragEvent, fieldId: string) => {
    e.dataTransfer.setData("text/plain", fieldId);
    e.dataTransfer.effectAllowed = "move";
    (e.target as HTMLElement).style.opacity = "0.4";
  };

  const handleDragEnd = (e: React.DragEvent) => {
    (e.target as HTMLElement).style.opacity = "1";
    setDragOverTarget(null);
  };

  const handleDragOver = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverTarget !== targetId) setDragOverTarget(targetId);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    const related = e.relatedTarget as Node | null;
    if (!related || !(e.currentTarget as Node).contains(related)) {
      setDragOverTarget(null);
    }
  };

  const handleDrop = (e: React.DragEvent, targetSignerId: string | null) => {
    e.preventDefault();
    setDragOverTarget(null);
    const fieldId = e.dataTransfer.getData("text/plain");
    if (!fieldId) return;

    if (targetSignerId) {
      setAssignments((prev) => ({ ...prev, [fieldId]: targetSignerId }));
    } else {
      setAssignments((prev) => {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
    }
  };

  /* ---- Click to cycle assignment ---- */

  const handleFieldClick = (fieldId: string) => {
    const current = assignments[fieldId];
    if (!current) {
      setAssignments((prev) => ({ ...prev, [fieldId]: "signer-1" }));
    } else {
      const num = parseInt(current.split("-")[1], 10);
      if (num >= signerCount) {
        setAssignments((prev) => {
          const next = { ...prev };
          delete next[fieldId];
          return next;
        });
      } else {
        setAssignments((prev) => ({
          ...prev,
          [fieldId]: `signer-${num + 1}`,
        }));
      }
    }
  };

  /* ---- Submit ---- */

  const handleSubmit = () => {
    const modifiedDoc = doc.map((page: any) => ({
      ...page,
      elements: (page.elements || []).map((el: any) => {
        if (
          (el.kind === "field" || el.kind === "signature") &&
          el.id &&
          assignments[el.id]
        ) {
          return { ...el, signerId: assignments[el.id] };
        }
        // Clear signer info for unassigned fields
        if (
          (el.kind === "field" || el.kind === "signature") &&
          el.id &&
          !assignments[el.id]
        ) {
          const { signerId, assignee, assigneeEmail, ...rest } = el;
          return rest;
        }
        return el;
      }),
    }));

    // Derive roles from signer assignments
    const roles = Array.from({ length: signerCount }, (_, i) => {
      const key = `signer-${i + 1}`;
      return {
        key,
        label: signerLabels[key] || `Signer ${i + 1}`,
        fieldIds: allFields
          .filter((f) => assignments[f.id] === key)
          .map((f) => f.id),
      };
    });

    // Derive senderFields from checked senderFills (Quick Send mode only)
    const derivedSenderFields =
      mode === "quicksend"
        ? allFields
            .filter((f) => senderFills[f.id])
            .map((f) => ({
              fieldId: f.id,
              label: f.label,
              type: f.kind === "field" ? "text" : "text",
            }))
        : [];

    onSave({
      name: templateName.trim() || "Untitled Template",
      signerLabels,
      doc: modifiedDoc,
      templateKind: mode,
      roles,
      senderFields: derivedSenderFields,
    });
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const hasFields = allFields.length > 0;

  // Check if the doc has any meaningful content (text, images, fields, signatures)
  const hasAnyContent = useMemo(() => {
    for (const page of doc) {
      const els: any[] = Array.isArray(page?.elements) ? page.elements : [];
      if (els.some((el) => el && el.kind)) return true;
    }
    return false;
  }, [doc]);

  if (!isOpen) return null;

  return (
    <div className="stm-backdrop" onMouseDown={handleBackdropClick}>
      <div className="stm-modal">
        {/* Header */}
        <div className="stm-header">
          <h2 className="stm-title">
            {mode === "quicksend" ? "Save as Quick Send Document" : "Save as Design Template"}
          </h2>
          <p className="stm-subtitle">
            {mode === "quicksend"
              ? "This document is ready to send as-is. Each time you use it, you just pick who fills each role."
              : hasFields
              ? "Name your template, set signers, and assign fields."
              : "Name your template and save it for reuse."}
          </p>
        </div>

        {/* Body */}
        <div className="stm-body">
          {/* Mode toggle */}
          <div className="stm-mode-section">
            <div className="stm-mode-toggle">
              <button
                type="button"
                className={`stm-mode-btn ${mode === "template" ? "stm-mode-active" : ""}`}
                onClick={() => setMode("template")}
              >
                Design Template
              </button>
              <button
                type="button"
                className={`stm-mode-btn ${mode === "quicksend" ? "stm-mode-active" : ""}`}
                onClick={() => setMode("quicksend")}
              >
                Quick Send Document
              </button>
            </div>
            <p className="stm-mode-hint">
              {mode === "quicksend"
                ? "Best for documents you send repeatedly without changes, like offer letters or standard NDAs."
                : "Best for documents that change between uses, like proposals or contracts with custom terms."}
            </p>
          </div>

          {/* Template name */}
          <div className="stm-name-section">
            <label className="stm-label">Template name</label>
            <input
              ref={nameInputRef}
              className="stm-name-input"
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Untitled Template"
            />
          </div>

          {hasFields && (
            <>
              {/* Signer count */}
              <div className="stm-count-section">
                <label className="stm-label">How many signers?</label>
                <div className="stm-count-controls">
                  <button
                    type="button"
                    className="stm-count-btn"
                    onClick={() => setSignerCount((c) => Math.max(1, c - 1))}
                    disabled={signerCount <= 1}
                  >
                    −
                  </button>
                  <span className="stm-count-value">{signerCount}</span>
                  <button
                    type="button"
                    className="stm-count-btn"
                    onClick={() => setSignerCount((c) => Math.min(10, c + 1))}
                    disabled={signerCount >= 10}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Quick Send explainer callout */}
              {mode === "quicksend" && (
                <div className="stm-qs-callout">
                  <div className="stm-qs-callout-title">Who fills each field?</div>
                  <div className="stm-qs-callout-text">
                    By default, the signer fills every field assigned to them. If you want to
                    fill in a field yourself at the time of sending (like a salary or start date),
                    check <strong>"I fill this"</strong> on that field below.
                  </div>
                </div>
              )}

              {/* Unassigned fields pool */}
              {unassignedFields.length > 0 && (
                <div
                  className={`stm-zone stm-unassigned ${
                    dragOverTarget === "unassigned" ? "stm-drop-active" : ""
                  }`}
                  onDragOver={(e) => handleDragOver(e, "unassigned")}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, null)}
                >
                  <div className="stm-zone-header">
                    <span className="stm-zone-label">Unassigned fields</span>
                    <span className="stm-zone-count">
                      {unassignedFields.length}
                    </span>
                  </div>
                  <div className="stm-field-list">
                    {unassignedFields.map((f) => (
                      <div
                        key={f.id}
                        className="stm-field-chip stm-field-unassigned"
                        draggable
                        onDragStart={(e) => handleDragStart(e, f.id)}
                        onDragEnd={handleDragEnd}
                        onClick={() => handleFieldClick(f.id)}
                        title="Click or drag to assign"
                      >
                        <span className="stm-field-icon">
                          {f.kind === "signature" ? "✍" : "T"}
                        </span>
                        <span className="stm-field-name">{f.label}</span>
                        <span className="stm-field-page">
                          p{f.pageIndex + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Signer cards */}
              {signers.map((signer, idx) => (
                <div
                  key={signer.id}
                  className={`stm-zone stm-signer-card ${
                    dragOverTarget === signer.id ? "stm-drop-active" : ""
                  }`}
                  style={{
                    borderColor:
                      dragOverTarget === signer.id
                        ? signer.color.dark
                        : undefined,
                  }}
                  onDragOver={(e) => handleDragOver(e, signer.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, signer.id)}
                >
                  <div className="stm-signer-top">
                    <span
                      className="stm-signer-dot"
                      style={{ background: signer.color.dark }}
                    />
                    <input
                      className="stm-signer-input"
                      type="text"
                      value={signer.label}
                      onChange={(e) =>
                        setSignerLabels((prev) => ({
                          ...prev,
                          [signer.id]: e.target.value,
                        }))
                      }
                      placeholder={`Signer ${idx + 1}`}
                    />
                    <span className="stm-signer-badge">
                      {signer.fields.length} field
                      {signer.fields.length === 1 ? "" : "s"}
                    </span>
                  </div>

                  {signer.fields.length > 0 ? (
                    <div className="stm-field-list">
                      {signer.fields.map((f) => {
                        const isSenderFill = mode === "quicksend" && senderFills[f.id];
                        return (
                          <div
                            key={f.id}
                            className={`stm-field-chip${isSenderFill ? " stm-field-sender" : ""}`}
                            style={{
                              borderLeftColor: isSenderFill ? "#d97706" : signer.color.dark,
                              background: isSenderFill ? "#fffbeb" : signer.color.soft,
                            }}
                            draggable
                            onDragStart={(e) => handleDragStart(e, f.id)}
                            onDragEnd={handleDragEnd}
                            onClick={() => handleFieldClick(f.id)}
                            title="Click or drag to reassign"
                          >
                            <span className="stm-field-icon">
                              {f.kind === "signature" ? "✍" : "T"}
                            </span>
                            <span className="stm-field-name">{f.label}</span>
                            {isSenderFill && (
                              <span className="stm-field-youfill">you fill</span>
                            )}
                            <span className="stm-field-page">
                              p{f.pageIndex + 1}
                            </span>
                            {mode === "quicksend" && (
                              <label
                                className="stm-sender-check"
                                onClick={(e) => e.stopPropagation()}
                                title="You will enter this value each time you send this document. The signer will see it pre-filled."
                              >
                                <input
                                  type="checkbox"
                                  checked={!!senderFills[f.id]}
                                  onChange={(e) =>
                                    setSenderFills((prev) => ({
                                      ...prev,
                                      [f.id]: e.target.checked,
                                    }))
                                  }
                                />
                                I fill this
                              </label>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="stm-signer-empty">
                      Drag fields here or click a field to assign
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {!hasFields && (
            <div className="stm-no-fields">
              No fillable fields found in this document. The template will be
              saved as-is.
            </div>
          )}

          {/* Sender fills summary (Quick Send only) */}
          {mode === "quicksend" && hasFields && (() => {
            const senderFieldNames = allFields
              .filter((f) => senderFills[f.id])
              .map((f) => f.label);
            if (senderFieldNames.length === 0) return null;
            return (
              <div className="stm-sender-summary">
                <strong>At send time, you will fill:</strong>{" "}
                {senderFieldNames.join(", ")}
              </div>
            );
          })()}
        </div>

        {/* Footer */}
        <div className="stm-footer">
          {!hasAnyContent && (
            <span className="stm-footer-warning">
              Add some content to your document before saving.
            </span>
          )}
          <button type="button" className="stm-btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="stm-btn-save"
            disabled={saving || !hasAnyContent}
            onClick={handleSubmit}
          >
            {saving
              ? "Saving..."
              : mode === "quicksend"
              ? "Save Quick Send Document"
              : "Save Template"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaveTemplateModal;
